/* -------------------------------------------------------------------------
 * Sayuri Wishlist — in-house, localStorage-backed save state.
 * -------------------------------------------------------------------------
 * Replaces the Wishlist Plus / Swym app embed block that previously
 * injected the dark plum 'ADD TO WISHLIST' button into the PDP.
 *
 * Public API (window.SayuriWishlist):
 *   .add(handle)     adds a product handle if not already saved
 *   .remove(handle)  removes a product handle
 *   .toggle(handle)  flips state; returns the NEW state (true=saved)
 *   .has(handle)     boolean
 *   .list()          array of saved handles in save order
 *   .count()         array length
 *
 * Side channels:
 *   window event 'sayuri:wishlist-changed' — fires on any mutation, this
 *     tab. Listen with addEventListener('sayuri:wishlist-changed', ...).
 *   storage event — fired by the browser when ANOTHER tab mutates the
 *     same key. We listen and re-sync local UI.
 *
 * Auto-wiring:
 *   - Document-level click delegation handles any .sayuri-wl-control on
 *     the page. Each control carries data-wl-handle. No per-button
 *     listener wiring needed by the templates.
 *   - On state change, every control on the page is re-synced (aria-
 *     pressed, .is-saved class, label text), and the header heart icon
 *     ([data-wishlist-icon]) is repainted (filled when count > 0, badge
 *     count refreshed and shown/hidden).
 *
 * Animation:
 *   window.__sayuriFlyToWishlist(x, y) — public for any future control
 *   that wants to trigger the same heart-flight animation. Called
 *   automatically on add (not on remove) from the click delegate. Skips
 *   the flight entirely under prefers-reduced-motion (still lands).
 *
 * Persistence key:
 *   sayuri:wishlist:v1 — versioned so we can ship breaking schema
 *   changes without colliding with existing user data. Bump the suffix
 *   if the on-disk shape ever changes.
 *
 * Idempotent: re-running this script in the same page is a no-op (early
 * exit via window.SayuriWishlist sentinel).
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.SayuriWishlist) return; // load once

  var KEY        = 'sayuri:wishlist:v1';
  var CHANGE_EVT = 'sayuri:wishlist-changed';
  var ACCENT     = '#A43A72';
  var ACCENT_SOFT = '#FFC4D1';
  // Heart geometry matches the Sayuri heart-compact silhouette (see
  // header.liquid's existing nav heart). Keep the path in sync if the
  // visual identity ever changes.
  var HEART_PATH =
    'M9 15.5 C 3 12, 1.5 7.5, 4 4.5 C 5.5 2.8, 7.8 3.5, 9 5 ' +
    'C 10.2 3.5, 12.5 2.8, 14 4.5 C 16.5 7.5, 15 12, 9 15.5 Z';

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(function (h) { return typeof h === 'string'; }) : [];
    } catch (e) { return []; }
  }
  function write(arr) {
    try { localStorage.setItem(KEY, JSON.stringify(arr)); } catch (e) {}
    window.dispatchEvent(new CustomEvent(CHANGE_EVT));
  }

  var Wishlist = {
    has:   function (h) { return read().indexOf(h) !== -1; },
    list:  function () { return read(); },
    count: function () { return read().length; },
    add: function (h) {
      var a = read();
      if (a.indexOf(h) === -1) { a.push(h); write(a); }
      return a.length;
    },
    remove: function (h) {
      var a = read();
      var i = a.indexOf(h);
      if (i !== -1) { a.splice(i, 1); write(a); }
      return a.length;
    },
    toggle: function (h) {
      var added = !this.has(h);
      if (added) this.add(h); else this.remove(h);
      return added;
    }
  };
  window.SayuriWishlist = Wishlist;

  // ─── UI sync helpers ────────────────────────────────────────────────
  function syncHeader() {
    var icon = document.querySelector('[data-wishlist-icon]');
    if (!icon) return;
    var n = Wishlist.count();
    // The heart stays OUTLINE in every state — matching the cart bag.
    // The count badge (shared cart-badge styling) signals contents; the
    // old filled-heart treatment made the two icons look unrelated.
    var badge = icon.querySelector('[data-wishlist-badge]');
    if (badge) {
      badge.textContent = String(n);
      badge.style.opacity = n > 0 ? '1' : '0';
      badge.style.transform = n > 0 ? 'scale(1)' : 'scale(0)';
    }
  }
  function syncControls() {
    var nodes = document.querySelectorAll('.sayuri-wl-control');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var handle = node.getAttribute('data-wl-handle');
      if (!handle) continue;
      var saved = Wishlist.has(handle);
      node.classList.toggle('is-saved', saved);
      node.setAttribute('aria-pressed', saved ? 'true' : 'false');
      // Update label text on the text-link variant. Whole phrase
      // (not just "Save"/"Saved") so the underline runs continuously
      // across the entire label — single span, single text run.
      var label = node.querySelector('.sayuri-wl-control__label');
      if (label) label.textContent = saved ? 'Saved to wishlist' : 'Save to wishlist';
      var aria = node.getAttribute('data-wl-aria-on') || 'Remove from wishlist';
      var ariaOff = node.getAttribute('data-wl-aria-off') || 'Save to wishlist';
      node.setAttribute('aria-label', saved ? aria : ariaOff);
    }
  }

  // ─── Click delegation ───────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var ctrl = e.target.closest && e.target.closest('.sayuri-wl-control');
    if (!ctrl) return;
    e.preventDefault();
    var handle = ctrl.getAttribute('data-wl-handle');
    if (!handle) return;
    var added = Wishlist.toggle(handle);
    if (added && typeof window.__sayuriFlyToWishlist === 'function') {
      var r = ctrl.getBoundingClientRect();
      window.__sayuriFlyToWishlist(r.left + r.width / 2, r.top + r.height / 2);
    }
  });

  // ─── Fly-to-wishlist animation ──────────────────────────────────────
  // Public window function so it could be triggered manually (e.g. an
  // Add-to-Cart hook that also bumps wishlist count, future cross-app
  // integration, etc.). Honours prefers-reduced-motion — just lands the
  // state change without the flight when reduce is set.
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.__sayuriFlyToWishlist = function (sx, sy) {
    var wl = document.querySelector('[data-wishlist-icon]');
    if (!wl) return;
    var wr = wl.getBoundingClientRect();
    var tx = wr.left + wr.width / 2;
    var ty = wr.top  + wr.height / 2;

    function land() {
      wl.classList.add('wl-receiving');
      var badge = wl.querySelector('[data-wishlist-badge]');
      if (badge) {
        badge.classList.remove('wl-badge-pop');
        // Force reflow so animation restarts on rapid toggles
        void badge.offsetWidth;
        badge.classList.add('wl-badge-pop');
      }
      setTimeout(function () { wl.classList.remove('wl-receiving'); }, 800);
    }

    if (reduced) { land(); return; }

    var layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:10000;';
    document.body.appendChild(layer);

    // Soft accent cloud at the source — feels like a scent rising off
    // the click point before the petals follow.
    var cloud = document.createElement('div');
    cloud.style.cssText =
      'position:absolute;' +
      'left:' + sx + 'px;top:' + sy + 'px;' +
      'width:40px;height:40px;margin-left:-20px;margin-top:-20px;' +
      'border-radius:50%;' +
      'background:radial-gradient(circle,' + ACCENT_SOFT + ' 0%,' + ACCENT_SOFT + '00 70%);' +
      'animation:sayuri-wl-cloud 1300ms cubic-bezier(.1,.7,.3,1) forwards;';
    layer.appendChild(cloud);

    // Hearts arcing from source → header heart.
    var colors = [ACCENT, ACCENT_SOFT, '#FF9CB8', '#FFFFFF'];
    var N = 6;
    for (var i = 0; i < N; i++) {
      var ang  = (i / N) * Math.PI * 2;
      var sx2  = sx + Math.cos(ang) * 16;
      var sy2  = sy + Math.sin(ang) * 16;
      var size = 14 + Math.random() * 8;
      var color = colors[i % colors.length];
      var mx = (tx - sx2) / 2 + (Math.random() - 0.5) * 110;
      var my = (ty - sy2) / 2 - 130 - Math.random() * 40;
      var rot = (Math.random() - 0.5) * 200;

      var h = document.createElement('div');
      h.style.cssText =
        'position:absolute;' +
        'left:' + sx2 + 'px;top:' + sy2 + 'px;' +
        'width:' + size + 'px;height:' + size + 'px;' +
        'margin-left:' + (-size / 2) + 'px;margin-top:' + (-size / 2) + 'px;' +
        'opacity:0;transform:scale(.4);will-change:transform,opacity;' +
        '--tx:' + (tx - sx2) + 'px;' +
        '--ty:' + (ty - sy2) + 'px;' +
        '--mx:' + mx + 'px;' +
        '--my:' + my + 'px;' +
        '--rot:' + rot + 'deg;' +
        'animation:sayuri-wl-fly 1100ms ' + (60 + i * 60) + 'ms cubic-bezier(.45,.05,.3,1) forwards;';
      h.innerHTML =
        '<svg viewBox="0 0 18 18" width="100%" height="100%">' +
        '<path d="' + HEART_PATH + '" fill="' + color + '"/>' +
        '</svg>';
      layer.appendChild(h);
    }

    setTimeout(land, 1050);
    setTimeout(function () { if (layer.parentNode) layer.remove(); }, 2200);
  };

  // ─── Wishlist PAGE renderer ─────────────────────────────────────────
  // Activates only where sections/sayuri-wishlist-page.liquid put the
  // [data-wishlist-page] shell. Reads Wishlist.list() (localStorage today;
  // a server source can feed the same renderer later — this code never
  // touches storage directly, only the public API), fetches product JSON
  // per handle, and clones the section's <template> into cards. Fetched
  // products are cached so a remove re-renders instantly with no refetch
  // and no flash.
  var productCache = {};

  function money(cents) {
    try {
      var core = window.themeCore;
      if (core && core.utils && core.utils.formatMoney && core.objects && core.objects.shop) {
        return core.utils.formatMoney(cents, core.objects.shop.money_format);
      }
    } catch (e) {}
    return '$' + (cents / 100).toFixed(2);
  }

  function sizedImage(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=500';
  }

  function fetchProduct(handle) {
    if (productCache[handle] !== undefined) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { productCache[handle] = p; return p; })
      .catch(function () { productCache[handle] = null; return null; });
  }

  function addToCart(variantId, ctaEl, page) {
    var core = window.themeCore;
    var api = core && core.CartApi;
    if (api && typeof api.makeRequest === 'function') {
      ctaEl.classList.add('is-loading');
      return api.makeRequest(api.actions.ADD_TO_CART, { id: variantId, quantity: 1 })
        .then(function () { return api.makeRequest(api.actions.GET_CART); })
        .then(function () {
          ctaEl.classList.remove('is-loading');
          ctaEl.classList.add('is-added');
          ctaEl.textContent = page.getAttribute('data-added-label') || 'Added';
          if (core.EventBus && typeof core.EventBus.emit === 'function') {
            core.EventBus.emit('cart:drawer:refresh-and-open');
          }
          setTimeout(function () {
            ctaEl.classList.remove('is-added');
            ctaEl.textContent = page.getAttribute('data-add-label') || 'Add to bag';
          }, 1600);
        })
        .catch(function () {
          ctaEl.classList.remove('is-loading');
          var CNE = core.CartNotificationError;
          if (CNE && CNE.addNotification) { CNE.addNotification('Could not add to bag. Please try again.'); CNE.open(); }
        });
    }
    // No cart API: hard fallback to the product page.
    window.location.href = ctaEl.getAttribute('data-pdp-url') || '/cart';
    return Promise.resolve();
  }

  function buildCard(page, tpl, product) {
    var node = tpl.content.firstElementChild.cloneNode(true);
    var link  = node.querySelector('[data-wl-card-link]');
    var img   = node.querySelector('[data-wl-card-image]');
    var title = node.querySelector('[data-wl-card-title]');
    var price = node.querySelector('[data-wl-card-price]');
    var cta   = node.querySelector('[data-wl-card-cta]');
    var remove = node.querySelector('[data-wl-card-remove]');

    node.setAttribute('data-wl-card', product.handle);
    if (link)  { link.href = product.url; }
    if (img)   { img.src = sizedImage(product.featured_image); img.alt = product.title || ''; }
    if (title) { title.href = product.url; title.textContent = product.title || ''; }
    if (price) { price.textContent = money(product.price); }
    if (remove) { remove.setAttribute('aria-label', 'Remove ' + (product.title || 'item') + ' from wishlist'); }

    var singleVariant = product.variants && product.variants.length === 1;
    var buyable = singleVariant && product.variants[0] && product.variants[0].available;
    if (cta) {
      cta.setAttribute('data-pdp-url', product.url);
      if (buyable) {
        cta.textContent = page.getAttribute('data-add-label') || 'Add to bag';
        cta.href = product.url; // progressive-enhancement fallback
        cta.setAttribute('data-variant-id', product.variants[0].id);
        cta.setAttribute('role', 'button');
      } else if (!product.available) {
        cta.textContent = 'Sold out';
        cta.href = product.url;
        cta.classList.add('sayuri-wl-card__cta--ghost');
      } else {
        // multi-variant (or single but details needed) → choose on PDP
        cta.textContent = page.getAttribute('data-choose-label') || 'Choose options';
        cta.href = product.url;
        cta.classList.add('sayuri-wl-card__cta--ghost');
      }
    }
    return node;
  }

  function renderWishlistPage() {
    var page = document.querySelector('[data-wishlist-page]');
    if (!page) return;
    var grid    = page.querySelector('[data-wishlist-grid]');
    var empty   = page.querySelector('[data-wishlist-empty]');
    var loading = page.querySelector('[data-wishlist-loading]');
    var countEl = page.querySelector('[data-wishlist-page-count]');
    var tpl     = page.querySelector('[data-wishlist-card-template]');
    if (!grid || !tpl) return;

    var handles = Wishlist.list();
    if (!handles.length) {
      if (loading) loading.hidden = true;
      grid.hidden = true;
      if (empty) empty.hidden = false;
      if (countEl) countEl.hidden = true;
      return;
    }

    Promise.all(handles.map(fetchProduct)).then(function (products) {
      if (loading) loading.hidden = true;
      grid.innerHTML = '';
      var shown = 0;
      handles.forEach(function (handle, i) {
        var product = products[i];
        if (!product) return; // deleted/renamed product — skip its card
        grid.appendChild(buildCard(page, tpl, product));
        shown++;
      });
      if (shown === 0) {
        grid.hidden = true;
        if (empty) empty.hidden = false;
        if (countEl) countEl.hidden = true;
      } else {
        grid.hidden = false;
        if (empty) empty.hidden = true;
        if (countEl) {
          countEl.hidden = false;
          countEl.textContent = shown === 1 ? '1 item' : shown + ' items';
        }
      }
    });
  }

  // Delegated actions on the wishlist page (remove + add-to-cart).
  document.addEventListener('click', function (e) {
    var page = e.target.closest && e.target.closest('[data-wishlist-page]');
    if (!page) return;
    var removeBtn = e.target.closest('[data-wl-card-remove]');
    if (removeBtn) {
      e.preventDefault();
      var card = removeBtn.closest('[data-wl-card]');
      var handle = card && card.getAttribute('data-wl-card');
      if (handle) { Wishlist.remove(handle); } // CHANGE_EVT re-renders from cache
      return;
    }
    var cta = e.target.closest('[data-wl-card-cta][data-variant-id]');
    if (cta) {
      e.preventDefault();
      addToCart(Number(cta.getAttribute('data-variant-id')), cta, page);
    }
  });

  // ─── Wire up event listeners ────────────────────────────────────────
  window.addEventListener(CHANGE_EVT, function () { syncHeader(); syncControls(); renderWishlistPage(); });
  // Cross-tab sync: localStorage 'storage' events fire on OTHER tabs.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { syncHeader(); syncControls(); }
  });

  function init() { syncHeader(); syncControls(); renderWishlistPage(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
